'use client';

import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { cn } from '@/lib/utils';
import { BookingStatus, BookingPaymentStatus, BOOKING_STATUSES, TravellerAgeCategory } from '@/lib/bookings/types';
import { Stay } from '@/lib/stays/api';
import { Trip } from '@/lib/trips/api';
import StaysSearchForm, { StaySearchParams } from '@/components/Stays/StaysSearchForm';
import TripSearchForm, { TripSearchParams } from '@/components/Trips/TripSearchForm';
import TripResultsGrid from '@/components/Trips/TripResultsGrid';
import TripAvailabilityChecker from '@/components/Trips/TripAvailabilityChecker';
import { getStays } from '@/lib/stays/api';
import { BookingSummary } from '@/components/ui/BookingSummary';
import InputGroup from '@/components/FormElements/InputGroup';
import { Select } from '@/components/FormElements/select';
import { TextArea } from '@/components/FormElements/textarea';
import { ShowcaseSection } from '@/components/Layouts/showcase-section';

const STEPS = [
  { id: 1, title: 'Module', description: 'Select booking type' },
  { id: 2, title: 'Search & Select', description: 'Find and select resource' },
  { id: 3, title: 'Guest Details', description: 'Customer & travellers' },
  { id: 4, title: 'Review', description: 'Confirm booking' },
];

const MODULE_TYPES = [
  { value: 'STAY', label: '🏨 Stay' },
  { value: 'TRIP', label: '🗺️ Trip' },
];

const TITLE_OPTIONS = [
  { value: 'Mr', label: 'Mr' },
  { value: 'Mrs', label: 'Mrs' },
  { value: 'Ms', label: 'Ms' },
  { value: 'Miss', label: 'Miss' },
  { value: 'Dr', label: 'Dr' },
];

const AGE_CATEGORY_OPTIONS = [
  { value: TravellerAgeCategory.ADULT, label: 'Adult' },
  { value: TravellerAgeCategory.CHILD, label: 'Child' },
  { value: TravellerAgeCategory.INFANT, label: 'Infant' },
];

interface Customer {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  phoneCode?: string;
}

interface SelectedResource {
  id: string;
  name: string;
  type: 'STAY' | 'TRIP';
  price?: number;
  startDate?: string;
  endDate?: string;
}

interface TravellerInput {
  title?: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  passportNumber?: string;
  ageCategory: TravellerAgeCategory;
}

interface StepperState {
  // Step 1
  moduleType: string;
  // Step 2
  selectedResource: SelectedResource | null;
  staySearchParams: StaySearchParams | null;
  stays: Stay[];
  staysLoading: boolean;
  tripSearchParams: TripSearchParams | null;
  selectedTrip: Trip | null;
  showAvailabilityChecker: boolean;
  // Step 3
  customerId: string;
  startDate: string;
  endDate: string;
  description: string;
  specialRequests: string;
  status: BookingStatus;
  travellers: TravellerInput[];
}

const PRIMARY_MODULE_TYPES = MODULE_TYPES.filter(m => m.value === 'STAY' || m.value === 'TRIP');

export default function BookingStepper() {
  const router = useRouter();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);

  // Customer autocomplete state
  const [customerSearchText, setCustomerSearchText] = useState('');
  const [customerSuggestions, setCustomerSuggestions] = useState<Customer[]>([]);
  const [showCustomerSuggestions, setShowCustomerSuggestions] = useState(false);
  const [customerLoading, setCustomerLoading] = useState(false);
  const customerAutocompleteRef = useRef<HTMLDivElement>(null);

  // Persisted stepper state
  const [state, setState] = useState<StepperState>({
    moduleType: 'STAY',
    selectedResource: null,
    staySearchParams: null,
    stays: [],
    staysLoading: false,
    tripSearchParams: null,
    selectedTrip: null,
    showAvailabilityChecker: false,
    customerId: '',
    startDate: '',
    endDate: '',
    description: '',
    specialRequests: '',
    status: BookingStatus.PENDING,
    travellers: [],
  });

  useEffect(() => {
    fetchCustomers();
  }, []);

  // Fetch stays when search params change
  useEffect(() => {
    if (state.moduleType === 'STAY' && state.staySearchParams) {
      fetchStays();
    }
  }, [state.staySearchParams]);

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem('admin_auth_token');
      const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/customers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setCustomers(data.data || []);
    } catch (err) {
      console.error('Failed to fetch customers:', err);
    }
  };

  // Close customer autocomplete when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (customerAutocompleteRef.current && !customerAutocompleteRef.current.contains(event.target as Node)) {
        setShowCustomerSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Customer search with autocomplete (debounced) - simple case insensitive search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (customerSearchText.length >= 2) {
        setCustomerLoading(true);
        const search = customerSearchText.toLowerCase();
        
        // Simple search by firstname, lastname, or email
        const filtered = customers.filter(c =>
          c.firstName.toLowerCase().includes(search) ||
          c.lastName.toLowerCase().includes(search) ||
          c.email.toLowerCase().includes(search)
        );
        
        setCustomerSuggestions(filtered.slice(0, 10));
        setShowCustomerSuggestions(true);
        setCustomerLoading(false);
      } else {
        setCustomerSuggestions([]);
        setShowCustomerSuggestions(false);
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [customerSearchText, customers]);

  const handleCustomerSelect = (customer: Customer) => {
    setCustomerSearchText(`${customer.firstName} ${customer.lastName}`);
    setState(prev => ({ ...prev, customerId: customer.id }));
    setShowCustomerSuggestions(false);
  };

  const fetchStays = async () => {
    if (!state.staySearchParams) return;

    setState(prev => ({ ...prev, staysLoading: true }));
    try {
      const params: any = { limit: 20, is_active: 1 };
      if (state.staySearchParams.destination) params.city = state.staySearchParams.destination;

      const response = await getStays(params);
      if (response?.data) {
        setState(prev => ({ ...prev, stays: response.data, staysLoading: false }));
      }
    } catch (err) {
      console.error('Failed to fetch stays:', err);
      setState(prev => ({ ...prev, staysLoading: false }));
    }
  };

  const updateState = <K extends keyof StepperState>(key: K, value: StepperState[K]) => {
    setState(prev => ({ ...prev, [key]: value }));
  };

  const handleModuleTypeChange = (type: string) => {
    updateState('moduleType', type);
    updateState('selectedResource', null);
    updateState('selectedTrip', null);
    updateState('showAvailabilityChecker', false);
    updateState('staySearchParams', null);
    updateState('tripSearchParams', null);
    updateState('stays', []);
  };

  const handleStaySearch = (params: StaySearchParams) => {
    updateState('staySearchParams', params);
    updateState('selectedResource', null);
  };

  const handleSelectStay = (stay: Stay) => {
    updateState('selectedResource', {
      id: stay.id || '',
      name: stay.name,
      type: 'STAY',
      price: stay.rooms?.[0]?.price,
    });
  };

  const handleTripSearch = (params: TripSearchParams) => {
    updateState('tripSearchParams', params);
    updateState('selectedTrip', null);
    updateState('showAvailabilityChecker', false);
    updateState('selectedResource', null);
  };

  const handleSelectTrip = (trip: Trip) => {
    updateState('selectedTrip', trip);
    updateState('showAvailabilityChecker', false);
    updateState('selectedResource', {
      id: trip.id || '',
      name: trip.name,
      type: 'TRIP',
      price: trip.pricing?.adult_price || 0,
    });
  };

  const handleConfirmTripSelection = () => {
    if (state.selectedTrip) {
      updateState('showAvailabilityChecker', true);
    }
  };

  const handleAvailabilityCheckComplete = (startDate: string, endDate: string) => {
    const current = state.selectedResource;
    if (current) {
      updateState('selectedResource', {
        ...current,
        startDate,
        endDate,
      });
    }
  };

  const handleAddTraveller = () => {
    updateState('travellers', [
      ...state.travellers,
      { firstName: '', lastName: '', ageCategory: TravellerAgeCategory.ADULT },
    ]);
  };

  const handleRemoveTraveller = (index: number) => {
    updateState('travellers', state.travellers.filter((_, i) => i !== index));
  };

  const handleTravellerChange = (index: number, field: keyof TravellerInput, value: string) => {
    updateState('travellers', state.travellers.map((t, i) =>
      i === index ? { ...t, [field]: value } : t
    ));
  };

  const validateStep = (step: number): string | null => {
    switch (step) {
      case 1:
        if (!state.moduleType) return 'Please select a booking module type';
        return null;
      case 2:
        if ((state.moduleType === 'STAY' || state.moduleType === 'TRIP') && !state.selectedResource) {
          return `Please select a ${state.moduleType === 'STAY' ? 'stay' : 'trip'} before proceeding`;
        }
        return null;
      case 3:
        if (!state.customerId) return 'Please select a customer';
        if (!state.startDate || !state.endDate) return 'Start and end dates are required';
        return null;
      case 4:
        return null;
      default:
        return null;
    }
  };

  const handleNext = () => {
    const validationError = validateStep(currentStep);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    setCurrentStep(prev => Math.min(prev + 1, 4));
  };

  const handleBack = () => {
    setError(null);
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    if (!state.customerId || !state.startDate || !state.endDate) {
      setError('Customer and dates are required');
      setLoading(false);
      return;
    }

    if ((state.moduleType === 'STAY' || state.moduleType === 'TRIP') && !state.selectedResource) {
      setError(`Please select a ${state.moduleType === 'STAY' ? 'stay' : 'trip'} before creating the booking`);
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('admin_auth_token');
      const bookingModuleType = MODULE_TYPES.find(m => m.value === state.moduleType);
      const notes = state.specialRequests ? [state.specialRequests] : [];

      const data = {
        customerId: state.customerId,
        description: state.description || undefined,
        notes,
        startDate: new Date(state.startDate).toISOString(),
        endDate: new Date(state.endDate).toISOString(),
        items: [{
          resourceId: state.selectedResource?.id || '00000000-0000-0000-0000-000000000000',
          resourceName: state.selectedResource?.name || bookingModuleType?.label || 'Booking',
          quantity: 1,
          unitPrice: Number(state.selectedResource?.price) || 0,
          startDate: new Date(state.startDate).toISOString(),
          endDate: new Date(state.endDate).toISOString(),
        }],
      };

      const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      if (res.ok) {
        const result = await res.json();
        router.push(`/bookings/${result.data?.id || ''}`);
      } else {
        const err = await res.json();
        setError(err.message || 'Failed to create booking');
        setLoading(false);
      }
    } catch (err) {
      console.error('Error creating booking:', err);
      setError('Failed to create booking. Please try again.');
      setLoading(false);
    }
  };

  const customerOptions = customers.map(c => ({
    value: c.id,
    label: `${c.firstName} ${c.lastName} (${c.email})`,
  }));

  const buildReviewBooking = () => {
    const customer = customers.find(c => c.id === state.customerId);
    const unitPrice = state.selectedResource?.price || 0;
    const quantity = 1;
    const totalPrice = unitPrice * quantity;
    return {
      id: 'preview',
      bookingNumber: 'NEW-BOOKING',
      customer: customer ? {
        id: customer.id,
        firstName: customer.firstName,
        lastName: customer.lastName,
        email: customer.email,
      } : null,
      customerId: state.customerId || '',
      startDate: state.startDate,
      endDate: state.endDate,
      status: state.status,
      paymentStatus: BookingPaymentStatus.UNPAID,
      subtotal: totalPrice,
      taxes: 0,
      discountAmount: 0,
      discountPercent: 0,
      totalAmount: totalPrice,
      items: state.selectedResource ? [{
        id: 'preview-item',
        resourceId: state.selectedResource.id,
        resourceName: state.selectedResource.name,
        quantity,
        unitPrice,
        totalPrice,
        discountPercent: 0,
        discountAmount: 0,
        startDate: state.startDate,
        endDate: state.endDate,
      }] : [],
      travellers: state.travellers,
      description: state.description,
      notes: state.specialRequests ? [state.specialRequests] : [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  };

  // --- Step 1: Module Selection ---
  const renderStep1 = () => (
    <ShowcaseSection title="Select Booking Module" className="!p-5.5">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {PRIMARY_MODULE_TYPES.map(type => (
          <button
            key={type.value}
            type="button"
            onClick={() => handleModuleTypeChange(type.value)}
            className={cn(
              "rounded-lg border px-3 py-3 text-sm transition",
              state.moduleType === type.value
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-stroke text-dark hover:border-gray-400 dark:border-dark-3 dark:text-white dark:hover:bg-dark-2'
            )}
          >
            {type.label}
          </button>
        ))}
      </div>
      <div className="mt-6 grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-7">
        {MODULE_TYPES.filter(m => m.value !== 'STAY' && m.value !== 'TRIP').map(type => (
          <button
            key={type.value}
            type="button"
            onClick={() => handleModuleTypeChange(type.value)}
            className={cn(
              "rounded-lg border px-2 py-2 text-xs transition",
              state.moduleType === type.value
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-stroke text-dark hover:border-gray-400 dark:border-dark-3 dark:text-white dark:hover:bg-dark-2'
            )}
          >
            {type.label}
          </button>
        ))}
      </div>
    </ShowcaseSection>
  );

  // --- Step 2: Search & Select ---
  const renderStep2 = () => (
    <div className="space-y-6">
      <ShowcaseSection title="Booking Type" className="!p-5.5">
        <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-2 text-sm font-medium text-primary">
          {MODULE_TYPES.find(m => m.value === state.moduleType)?.label}
        </div>
      </ShowcaseSection>

      {state.moduleType === 'STAY' && (
        <ShowcaseSection title="Search Stays" className="!p-5.5">
          <StaysSearchForm onSearch={handleStaySearch} />

          {state.staySearchParams && (
            <div className="mt-6 space-y-4">
              <h4 className="text-sm font-medium text-dark dark:text-white">
                {state.staysLoading ? 'Loading stays...' : `Found ${state.stays.length} stays`}
              </h4>

              {state.staysLoading ? (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="animate-pulse rounded-lg bg-white p-4 shadow dark:bg-dark-2">
                      <div className="h-32 bg-gray-200 dark:bg-dark-3 rounded mb-3" />
                      <div className="h-4 bg-gray-200 dark:bg-dark-3 rounded w-3/4 mb-2" />
                      <div className="h-3 bg-gray-200 dark:bg-dark-3 rounded w-1/2" />
                    </div>
                  ))}
                </div>
              ) : state.stays.length > 0 ? (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {state.stays.map(stay => (
                    <div
                      key={stay.id}
                      onClick={() => handleSelectStay(stay)}
                      className={cn(
                        "cursor-pointer rounded-lg bg-white p-4 shadow transition hover:shadow-md dark:bg-dark-2",
                        state.selectedResource?.id === stay.id && state.selectedResource?.type === 'STAY'
                          ? 'ring-2 ring-primary'
                          : ''
                      )}
                    >
                      {stay.thumbnail_url ? (
                        <img
                          src={stay.thumbnail_url}
                          alt={stay.name}
                          className="h-32 w-full object-cover rounded mb-3"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="h-32 w-full bg-gray-200 dark:bg-dark-3 rounded mb-3 flex items-center justify-center">
                          <span className="text-2xl font-bold text-gray-400">{stay.name.substring(0, 2).toUpperCase()}</span>
                        </div>
                      )}
                      <h5 className="font-medium text-dark dark:text-white">{stay.name}</h5>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {stay.location?.city}, {stay.location?.country}
                      </p>
                      <div className="mt-2 flex items-center justify-between">
                        <span className="text-sm font-medium text-primary">
                          {stay.accommodation_type} {'★'.repeat(stay.stars)}
                        </span>
                        {stay.rooms?.[0]?.price && (
                          <span className="text-sm font-medium text-dark dark:text-white">
                            ${stay.rooms[0].price}/night
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No stays found. Try a different search.
                </p>
              )}
            </div>
          )}

          {state.selectedResource?.type === 'STAY' && (
            <div className="mt-6 rounded-lg bg-green-50 border border-green-200 p-4 dark:bg-green-900/20 dark:border-green-800">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-800 dark:text-green-400">Selected Stay:</p>
                  <p className="font-medium text-green-900 dark:text-green-300">{state.selectedResource.name}</p>
                  {state.selectedResource.price && (
                    <p className="text-sm text-green-700 dark:text-green-500">
                      ${state.selectedResource.price}/night
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => updateState('selectedResource', null)}
                  className="text-sm text-red-600 hover:text-red-800 dark:text-red-400"
                >
                  Remove
                </button>
              </div>
            </div>
          )}
        </ShowcaseSection>
      )}

      {state.moduleType === 'TRIP' && (
        <ShowcaseSection title="Search Trips" className="!p-5.5">
          <TripSearchForm onSearch={handleTripSearch} />

          {state.tripSearchParams && (
            <div className="mt-6">
              <TripResultsGrid
                searchParams={state.tripSearchParams}
                selectedTripId={state.selectedTrip?.id}
                onSelectTrip={handleSelectTrip}
              />
            </div>
          )}

          {state.selectedTrip && (
            <div className="mt-6 space-y-4">
              <div className="rounded-lg bg-blue-50 border border-blue-200 p-4 dark:bg-blue-900/20 dark:border-blue-800">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-blue-800 dark:text-blue-400">Selected Trip:</p>
                    <p className="font-medium text-blue-900 dark:text-blue-300">
                      {state.selectedTrip.name}
                    </p>
                    {state.selectedTrip.pricing?.adult_price && (
                      <p className="text-sm text-blue-700 dark:text-blue-500">
                        ${state.selectedTrip.pricing.adult_price} per person
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      updateState('selectedTrip', null);
                      updateState('showAvailabilityChecker', false);
                      updateState('selectedResource', null);
                    }}
                    className="text-sm text-red-600 hover:text-red-800 dark:text-red-400"
                  >
                    Remove
                  </button>
                </div>
              </div>

              {!state.showAvailabilityChecker && (
                <button
                  type="button"
                  onClick={handleConfirmTripSelection}
                  className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-opacity-90"
                >
                  Check Availability
                </button>
              )}

              {state.showAvailabilityChecker && (
                <TripAvailabilityChecker
                  trip={state.selectedTrip}
                />
              )}
            </div>
          )}
        </ShowcaseSection>
      )}

      {state.moduleType !== 'STAY' && state.moduleType !== 'TRIP' && (
        <ShowcaseSection title="Resource Details" className="!p-5.5">
          <label className="mb-2 block text-sm font-medium text-dark dark:text-white">
            Resource / Service Name
          </label>
          <InputGroup
            label=""
            placeholder="e.g. Grand Hotel, Flight to NYC"
            type="text"
            value={state.selectedResource?.name || ''}
            handleChange={(e) => updateState('selectedResource', {
              id: '',
              name: e.target.value,
              type: state.moduleType as 'STAY' | 'TRIP',
            })}
          />
        </ShowcaseSection>
      )}
    </div>
  );

  // --- Step 3: Guest Details ---
  const renderStep3 = () => (
    <div className="space-y-6">
      <ShowcaseSection title="Guest Information" className="!p-5.5">
        <div className="grid grid-cols-1 gap-4">
          <div className="relative" ref={customerAutocompleteRef}>
            <label className="mb-3 block text-body-sm font-medium text-dark dark:text-white">
              Customer <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type="text"
                value={customerSearchText}
                onChange={(e) => setCustomerSearchText(e.target.value)}
                onFocus={() => customerSearchText.length >= 2 && setShowCustomerSuggestions(true)}
                placeholder="Search customer by name or email..."
                className="w-full rounded-lg border-[1.5px] border-stroke bg-transparent px-5 py-3 outline-none transition focus:border-primary active:border-primary dark:border-dark-3 dark:bg-dark-2 dark:focus:border-primary"
              />
              
              {/* Suggestions Dropdown */}
              {showCustomerSuggestions && (customerSearchText.length >= 2) && (
                <div className="absolute z-50 mt-1 w-full rounded-lg border border-stroke bg-white shadow-lg dark:border-dark-3 dark:bg-dark-2">
                  {customerLoading ? (
                    <div className="px-5 py-3 text-sm text-gray-500 dark:text-gray-400">
                      Loading...
                    </div>
                  ) : customerSuggestions.length > 0 ? (
                    <ul className="max-h-60 overflow-auto py-1">
                      {customerSuggestions.map((customer) => (
                        <li
                          key={customer.id}
                          onClick={() => handleCustomerSelect(customer)}
                          className="cursor-pointer px-5 py-2.5 text-sm hover:bg-gray-2 dark:hover:bg-dark dark:text-white"
                        >
                          {customer.firstName} {customer.lastName}
                          <span className="ml-2 text-gray-500">{customer.email}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="px-5 py-3 text-sm text-gray-500 dark:text-gray-400">
                      No customers found
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </ShowcaseSection>

      <ShowcaseSection title="Booking Details" className="!p-5.5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <InputGroup
              label="Start Date"
              type="date"
              name="startDate"
              value={state.startDate}
              placeholder=""
              handleChange={(e) => updateState('startDate', e.target.value)}
              required
            />
          </div>
          <div>
            <InputGroup
              label="End Date"
              type="date"
              name="endDate"
              value={state.endDate}
              placeholder=""
              handleChange={(e) => updateState('endDate', e.target.value)}
              required
            />
          </div>
          <div>
            <label className="mb-2 block text-sm font-medium text-dark dark:text-white">
              Status
            </label>
            <Select
              label=""
              items={BOOKING_STATUSES}
              value={state.status}
              onChange={(value) => updateState('status', value as BookingStatus)}
            />
          </div>
        </div>
        <div className="mt-4">
          <label className="mb-2 block text-sm font-medium text-dark dark:text-white">
            Description
          </label>
          <TextArea
            label=""
            placeholder="Enter booking description..."
            value={state.description}
            handleChange={(e) => updateState('description', e.target.value)}
          />
        </div>
        <div className="mt-4">
          <label className="mb-2 block text-sm font-medium text-dark dark:text-white">
            Special Requests
          </label>
          <TextArea
            label=""
            placeholder="Enter any special requests or notes for this booking..."
            value={state.specialRequests}
            handleChange={(e) => updateState('specialRequests', e.target.value)}
          />
        </div>
      </ShowcaseSection>

      <ShowcaseSection title="Travellers" className="!p-5.5">
        {state.travellers.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No travellers added yet. Click "Add Traveller" to add guest information.
          </p>
        ) : (
          <div className="space-y-4">
            {state.travellers.map((traveller, index) => (
              <div
                key={index}
                className="rounded-lg border border-stroke bg-gray-1 p-4 dark:border-dark-3 dark:bg-dark-2"
              >
                <div className="mb-4 flex items-center justify-between">
                  <span className="text-sm font-medium text-dark dark:text-white">
                    Traveller {index + 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleRemoveTraveller(index)}
                    className="text-sm text-red-600 hover:text-red-800 dark:text-red-400"
                  >
                    Remove
                  </button>
                </div>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
                  <div>
                    <label className="mb-2 block text-xs font-medium text-gray-600 dark:text-gray-400">
                      Title
                    </label>
                    <select
                      value={traveller.title || ''}
                      onChange={(e) => handleTravellerChange(index, 'title', e.target.value)}
                      className="w-full rounded-lg border border-stroke bg-white px-3 py-2 text-sm text-dark dark:border-dark-3 dark:bg-dark dark:text-white"
                    >
                      <option value="">Select</option>
                      {TITLE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-medium text-gray-600 dark:text-gray-400">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={traveller.firstName}
                      onChange={(e) => handleTravellerChange(index, 'firstName', e.target.value)}
                      required
                      className="w-full rounded-lg border border-stroke bg-white px-3 py-2 text-sm text-dark dark:border-dark-3 dark:bg-dark dark:text-white"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-medium text-gray-600 dark:text-gray-400">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={traveller.lastName}
                      onChange={(e) => handleTravellerChange(index, 'lastName', e.target.value)}
                      required
                      className="w-full rounded-lg border border-stroke bg-white px-3 py-2 text-sm text-dark dark:border-dark-3 dark:bg-dark dark:text-white"
                      placeholder="Doe"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-medium text-gray-600 dark:text-gray-400">
                      Email
                    </label>
                    <input
                      type="email"
                      value={traveller.email || ''}
                      onChange={(e) => handleTravellerChange(index, 'email', e.target.value)}
                      className="w-full rounded-lg border border-stroke bg-white px-3 py-2 text-sm text-dark dark:border-dark-3 dark:bg-dark dark:text-white"
                      placeholder="john@example.com"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-medium text-gray-600 dark:text-gray-400">
                      Phone
                    </label>
                    <input
                      type="tel"
                      value={traveller.phone || ''}
                      onChange={(e) => handleTravellerChange(index, 'phone', e.target.value)}
                      className="w-full rounded-lg border border-stroke bg-white px-3 py-2 text-sm text-dark dark:border-dark-3 dark:bg-dark dark:text-white"
                      placeholder="+1 234 5678"
                    />
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  <div>
                    <label className="mb-2 block text-xs font-medium text-gray-600 dark:text-gray-400">
                      Passport Number
                    </label>
                    <input
                      type="text"
                      value={traveller.passportNumber || ''}
                      onChange={(e) => handleTravellerChange(index, 'passportNumber', e.target.value)}
                      className="w-full rounded-lg border border-stroke bg-white px-3 py-2 text-sm text-dark dark:border-dark-3 dark:bg-dark dark:text-white"
                      placeholder="AB1234567"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-xs font-medium text-gray-600 dark:text-gray-400">
                      Age Category
                    </label>
                    <select
                      value={traveller.ageCategory}
                      onChange={(e) => handleTravellerChange(index, 'ageCategory', e.target.value)}
                      className="w-full rounded-lg border border-stroke bg-white px-3 py-2 text-sm text-dark dark:border-dark-3 dark:bg-dark dark:text-white"
                    >
                      {AGE_CATEGORY_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="mt-4">
          <button
            type="button"
            onClick={handleAddTraveller}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-dashed border-primary px-4 py-2 text-sm font-medium text-primary hover:bg-primary/5 dark:border-primary dark:text-primary"
          >
            + Add Traveller
          </button>
        </div>
      </ShowcaseSection>
    </div>
  );

  // --- Step 4: Review & Confirm ---
  const renderStep4 = () => {
    const reviewBooking = buildReviewBooking();
    return (
      <div className="space-y-6">
        <ShowcaseSection title="Booking Summary" className="!p-5.5">
          <BookingSummary
            booking={reviewBooking}
            moduleType={state.moduleType === 'STAY' ? 'stay' : 'trip'}
          />
        </ShowcaseSection>

        <ShowcaseSection title="Review Details" className="!p-5.5">
          <div className="space-y-4">
            {/* Customer */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Customer</p>
                <p className="text-sm text-dark dark:text-white">
                  {customers.find(c => c.id === state.customerId)
                    ? `${customers.find(c => c.id === state.customerId)?.firstName} ${customers.find(c => c.id === state.customerId)?.lastName}`
                    : '—'}
                </p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Status</p>
                <p className="text-sm text-dark dark:text-white">{state.status}</p>
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Start Date</p>
                <p className="text-sm text-dark dark:text-white">{state.startDate || '—'}</p>
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">End Date</p>
                <p className="text-sm text-dark dark:text-white">{state.endDate || '—'}</p>
              </div>
            </div>

            {/* Resource */}
            {state.selectedResource && (
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                  {state.moduleType === 'STAY' ? 'Stay' : 'Trip'}
                </p>
                <p className="text-sm text-dark dark:text-white">
                  {state.selectedResource.name}
                  {state.selectedResource.price && ` — $${state.selectedResource.price}`}
                </p>
              </div>
            )}

            {/* Description */}
            {state.description && (
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Description</p>
                <p className="text-sm text-dark dark:text-white">{state.description}</p>
              </div>
            )}

            {/* Special Requests */}
            {state.specialRequests && (
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Special Requests</p>
                <p className="text-sm text-dark dark:text-white">{state.specialRequests}</p>
              </div>
            )}

            {/* Travellers */}
            {state.travellers.length > 0 && (
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                  Travellers ({state.travellers.length})
                </p>
                <div className="mt-2 space-y-2">
                  {state.travellers.map((t, i) => (
                    <div key={i} className="text-sm text-dark dark:text-white">
                      {t.title && `${t.title} `}{t.firstName} {t.lastName}
                      <span className="ml-2 text-xs text-gray-500">({t.ageCategory})</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </ShowcaseSection>

        <div className="flex items-center justify-between rounded-lg border border-stroke bg-gray-1 p-4 dark:border-dark-3 dark:bg-dark-2">
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Booking Total</p>
            <p className="text-2xl font-bold text-dark dark:text-white">
              ${state.selectedResource?.price?.toLocaleString() || '0'}
            </p>
          </div>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-8 py-3 text-center font-medium text-white hover:bg-opacity-90 transition duration-200 ease-in-out disabled:opacity-50"
          >
            {loading ? 'Creating...' : 'Confirm & Create Booking'}
          </button>
        </div>
      </div>
    );
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      default: return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Progress Steps */}
      <div className="rounded-lg border border-stroke bg-white p-4 dark:border-dark-3 dark:bg-dark">
        <div className="mb-4 flex items-center justify-between">
          {STEPS.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <div className="flex flex-col items-center">
                <div
                  className={cn(
                    "flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium transition",
                    currentStep > step.id
                      ? 'bg-primary text-white'
                      : currentStep === step.id
                        ? 'bg-primary text-white ring-4 ring-primary/20'
                        : 'bg-gray-200 text-gray-500 dark:bg-dark-3 dark:text-gray-400'
                  )}
                >
                  {currentStep > step.id ? (
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    step.id
                  )}
                </div>
                <span className={cn(
                  "mt-1.5 text-xs font-medium",
                  currentStep === step.id
                    ? 'text-primary'
                    : 'text-gray-500 dark:text-gray-400'
                )}>
                  {step.title}
                </span>
              </div>
              {index < STEPS.length - 1 && (
                <div className={cn(
                  "mx-2 h-0.5 w-12 flex-shrink-0 sm:w-20 lg:w-32",
                  currentStep > step.id
                    ? 'bg-primary'
                    : 'bg-gray-200 dark:bg-dark-3'
                )} />
              )}
            </div>
          ))}
        </div>

        {/* Step Description */}
        <div className="border-t border-stroke pt-3 dark:border-dark-3">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Step {currentStep}: <span className="font-medium text-dark dark:text-white">
              {STEPS.find(s => s.id === currentStep)?.description}
            </span>
          </p>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="rounded-lg bg-red-100 p-4 text-red-800 dark:bg-red-900/20 dark:text-red-400">
          {error}
          <button
            type="button"
            onClick={() => setError(null)}
            className="ml-4 text-sm underline hover:no-underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Step Content */}
      <div>
        {renderCurrentStep()}
      </div>

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between border-t border-stroke pt-6 dark:border-dark-3">
        <div>
          {currentStep > 1 && (
            <button
              type="button"
              onClick={handleBack}
              className="inline-flex items-center gap-2 rounded-lg border border-stroke px-6 py-3 font-medium text-dark hover:bg-gray-2 dark:border-dark-3 dark:text-white dark:hover:bg-dark"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back
            </button>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => router.back()}
            className="inline-flex rounded-lg border border-stroke px-6 py-3 font-medium text-dark hover:bg-gray-2 dark:border-dark-3 dark:text-white dark:hover:bg-dark"
          >
            Cancel
          </button>
          {currentStep < 4 && (
            <button
              type="button"
              onClick={handleNext}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3 font-medium text-white hover:bg-opacity-90 transition"
            >
              Next
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
